<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings-users' }"
                        ><font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1">
                        {{ $t("mainmenu.settings") }} / {{ $t("settings.users.title") }} /
                        {{ $t("settings.users.edit.title") }}
                    </h1>

                    <b-alert
                        v-if="userData && userData.id && !userData.auth0Id"
                        class="mt-2 mb-2"
                        show
                        variant="warning"
                    >
                        <div class="alert-body">
                            <span>{{ $t("settings.users.edit.disabled-not-activated") }}</span>
                        </div>
                    </b-alert>

                    <hr />

                    <div v-if="isLoading" class="d-flex justify-content-center mb-3">
                        <b-spinner />
                    </div>

                    <validation-observer v-if="userData" ref="validationRules" name="user" tag="form">
                        <AppUserInfo :user-data="userData" />
                        <AppChannels :channels="channels" :user-data="userData" />
                        <AppTeams :teams="teams" :user-data="userData" />

                        <b-form-group content-cols-lg="7" content-cols-sm label-cols-lg="3" label-cols-sm="4">
                            <b-button
                                :disabled="isSaving || (userData && userData.id && !userData.auth0Id)"
                                class="d-flex justify-content-center mb-1"
                                variant="primary"
                                @click="edit"
                            >
                                <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                                {{ $t("settings.users.edit.submit") }}
                            </b-button>
                        </b-form-group>
                    </validation-observer>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>
<script>
import { BAlert, BButton, BFormInput, BContainer, BRow, BCol, BLink, BFormGroup, BSpinner } from "bootstrap-vue";
import { ref } from "@vue/composition-api";
import axios from "@/libs/axios";
import router from "@/router";
import AppUserInfo from "./components/userInfo";
import AppChannels from "./components/channels";
import AppTeams from "./components/teams";
import { ValidationObserver } from "vee-validate";
import useUsers from "./useUsers";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import useUsersForm from "@/views/modules/settings/users/useUsersForm";

export default {
    components: {
        BAlert,
        BButton,
        BFormInput,
        BRow,
        BContainer,
        BCol,
        BLink,
        BFormGroup,
        BSpinner,
        AppUserInfo,
        AppChannels,
        AppTeams,
        ValidationObserver,
    },
    setup() {
        const toast = useToast();
        const userData = ref(null);
        const { teams, channels, fetchTeams, fetchChannels } = useUsers();
        const { getUserSettingsAsArray, setUserSettings } = useUsersForm();
        const isLoading = ref(true);
        const isSaving = ref(false);

        fetchTeams();
        fetchChannels();

        axios
            .get(`/users/${router.currentRoute.params.id}`, {
                params: {
                    fields: "id,name,displayName,email,role,active,auth0Id",
                    join: ["channels", "teams"],
                },
            })
            .then((response) => {
                response.data.channels = response.data.channels.map((channel) => channel.id);
                response.data.teams = response.data.teams.map((team) => team.id);
                userData.value = response.data;
                setUserSettings(response.data.settings);
            })
            .catch(() => (userData.value = undefined))
            .finally(() => (isLoading.value = false));

        return {
            userData,
            channels,
            teams,
            toast,
            isLoading,
            isSaving,
            getUserSettingsAsArray,
        };
    },
    methods: {
        edit() {
            return new Promise((resolve, reject) => {
                this.$refs.validationRules.validate().then((success) => {
                    if (success) {
                        const userId = this.userData.id;

                        const channels = this.channels.map((channel) => {
                            const selectedChannel = this.userData.channels.find(
                                (channelId) => channelId === channel.id
                            );
                            if (
                                selectedChannel &&
                                channel.userChannels.every((userChannel) => userChannel.userId !== userId)
                            ) {
                                channel.userChannels.push({
                                    channelId: channel.id,
                                    userId,
                                });
                            } else if (!selectedChannel) {
                                channel.userChannels = channel.userChannels.filter(
                                    (userChannel) => userChannel.userId !== userId
                                );
                            }
                            return channel;
                        });

                        const teams = this.teams.map((team) => {
                            const selectedTeam = this.userData.teams.find((teamId) => teamId === team.id);
                            if (selectedTeam && team.teamUsers.every((teamUser) => teamUser.userId !== userId)) {
                                team.teamUsers.push({
                                    teamId: team.id,
                                    userId,
                                });
                            } else if (!selectedTeam) {
                                team.teamUsers = team.teamUsers.filter((teamUser) => teamUser.userId !== userId);
                            }
                            return team;
                        });

                        this.userData.settings = this.getUserSettingsAsArray().map((setting) => ({
                            ...setting,
                            userId,
                        }));

                        this.isSaving = true;

                        axios
                            .put(`users/${userId}`, {
                                ...this.userData,
                                channels,
                                teams,
                            })
                            .then((result) => {
                                this.$router.push({ name: "settings-users" }, () => {
                                    this.toast.success(i18n.t("settings.users.updated.success.title"));
                                });
                                resolve(result.data);
                            })
                            .catch(() => {
                                this.toast.error(
                                    i18n.t("toast.update.error", {
                                        entity: this.userData.name,
                                    })
                                );
                                reject();
                            })
                            .finally(() => (this.isSaving = false));

                        resolve();
                    } else {
                        this.toast.error(
                            i18n.t("toast.update.error", {
                                entity: this.userData.name,
                            })
                        );
                        reject();
                    }
                });
            });
        },
    },
};
</script>
